/**
 * Product Controls Accessibility
 *
 * Main improvements:
 * - removes disabled quantity buttons from keyboard navigation;
 * - synchronizes native disabled and aria-disabled states;
 * - removes sold-out variant radio buttons from keyboard navigation;
 * - updates states when variants or quantities change dynamically.
 */

(function () {
  /**
   * Updates Minus and Plus button states according to the current
   * quantity input minimum and maximum values.
   */
  function updateQuantityButtons(quantityWrapper) {
    if (!quantityWrapper) return;

    const input = quantityWrapper.querySelector(".m-quantity__input");

    const minusButton = quantityWrapper.querySelector('[name="minus"]');

    const plusButton = quantityWrapper.querySelector('[name="plus"]');

    if (!input || !minusButton || !plusButton) return;

    const value = Number(input.value);
    const min = Number(input.min || input.dataset.min || 1);
    const max = input.max ? Number(input.max) : null;

    const minusDisabled = value <= min;

    minusButton.disabled = minusDisabled;
    minusButton.setAttribute("aria-disabled", minusDisabled ? "true" : "false");

    if (minusDisabled) {
      minusButton.setAttribute("tabindex", "-1");
    } else {
      minusButton.removeAttribute("tabindex");
    }

    const plusDisabled = max !== null && !Number.isNaN(max) && value >= max;

    plusButton.disabled = plusDisabled;
    plusButton.setAttribute("aria-disabled", plusDisabled ? "true" : "false");

    if (plusDisabled) {
      plusButton.setAttribute("tabindex", "-1");
    } else {
      plusButton.removeAttribute("tabindex");
    }
  }

  function initQuantityButtons(root) {
    root.querySelectorAll("m-quantity-input").forEach(updateQuantityButtons);
  }

  /**
   * Initializes quantity state and keeps it synchronized after
   * manual input, button clicks and asynchronous product rendering.
   */
  function initQuantityAccessibility() {
    initQuantityButtons(document);

    document.addEventListener("input", function (event) {
      const input = event.target.closest(".m-quantity__input");

      if (!input) return;

      const wrapper = input.closest("m-quantity-input");

      updateQuantityButtons(wrapper);
    });

    document.addEventListener("change", function (event) {
      const input = event.target.closest(".m-quantity__input");

      if (!input) return;

      const wrapper = input.closest("m-quantity-input");

      updateQuantityButtons(wrapper);
    });

    document.addEventListener("click", function (event) {
      const button = event.target.closest(
        "m-quantity-input .m-quantity__button",
      );

      if (!button) return;

      const wrapper = button.closest("m-quantity-input");

      window.setTimeout(function () {
        updateQuantityButtons(wrapper);
      }, 0);
    });

    new MutationObserver(function () {
      initQuantityButtons(document);
    }).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Synchronizes sold-out variant appearance with its actual
   * keyboard and screen-reader state.
   */
  function syncSoldOutVariantOptions(root) {
    const scope = root || document;

    scope
      .querySelectorAll(".m-product-option--node")
      .forEach(function (optionNode) {
        const input = optionNode.querySelector('input[type="radio"]');

        if (!input) return;

        const isSoldOut =
          optionNode.classList.contains("m-product-option--node__soldout") ||
          optionNode.classList.contains("soldout") ||
          optionNode.getAttribute("aria-disabled") === "true";

        if (isSoldOut) {
          input.disabled = true;
          input.setAttribute("aria-disabled", "true");
          input.setAttribute("tabindex", "-1");
        } else {
          input.disabled = false;
          input.removeAttribute("aria-disabled");
          input.removeAttribute("tabindex");
        }
      });
  }

  /**
   * Watches variant pickers because sold-out classes can change
   * after another product option is selected.
   */
  function initSoldOutVariantAccessibility() {
    syncSoldOutVariantOptions(document);

    const observer = new MutationObserver(function (mutations) {
      const shouldUpdate = mutations.some(function (mutation) {
        return (
          mutation.type === "childList" ||
          mutation.attributeName === "class" ||
          mutation.attributeName === "aria-disabled"
        );
      });

      if (!shouldUpdate) return;

      requestAnimationFrame(function () {
        syncSoldOutVariantOptions(document);
      });
    });

    document.querySelectorAll("variant-picker").forEach(function (picker) {
      observer.observe(picker, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "aria-disabled"],
      });
    });

    document.addEventListener("change", function (event) {
      if (!event.target.closest("variant-picker")) return;

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          syncSoldOutVariantOptions(document);
        });
      });
    });
  }

  function initProductControlsAccessibility() {
    initQuantityAccessibility();
    initSoldOutVariantAccessibility();
  }

  window.ASA11y.onReady(initProductControlsAccessibility);
})();
